import {
  StylesProvider,
  ThemeProvider,
  jssPreset,
} from '@material-ui/core/styles'
import { create } from 'jss'
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3'
import CssBaseline from '@material-ui/core/CssBaseline'
import PropTypes from 'prop-types'
import React from 'react'
import rtl from 'jss-rtl'
import { injectIntl } from 'gatsby-plugin-intl'
import Header from './Header/Header'
import Footer from './Footer/Footer'
import FadeTransition from './FadeTransition'
import themeObject from '../assets/theme'
import SEO from './SEO'
import { isObjectEmpty } from '../utils/objects'

// Configure JSS
const jss = create({ plugins: [...jssPreset().plugins, rtl()] })

// https://github.com/mui-org/material-ui/tree/master/examples/gatsby
const Layout = ({ children, data = {}, location, ...props }) => {
  // footerSlash config
  const footerRef = React.useRef(null)
  const [footerNode, setFooterNode] = React.useState(footerRef?.current)
  React.useEffect(() => {
    setFooterNode(footerRef?.current)
  })
  // language
  const { locale: language } = props.intl

  // theme
  const theme = themeObject(language)

  // TODO: change instances of T function to use Intl FormatMessage instead
  const t = (value, params) =>
    value ? props.intl?.formatMessage({ id: value }, params) : ''

  // pageData
  const { header, footer, waitingList = {}, page = {}, ...rest } = data
  const { title, metaDescription, metaTag, ...pageData } = page || {}

  const siteKey = process.env.GATSBY_RECAPTCHA_SITE_KEY

  return (
    <React.Fragment key={language}>
      <SEO
        title={title}
        metaDescription={metaDescription}
        metaTag={metaTag}
        language={language}
        theme={theme}
        location={location}
      />
      <GoogleReCaptchaProvider
        reCaptchaKey={siteKey}
        container={{
          parameters: {
            theme: 'light', // optional, default undefined
          },
        }}
      >
        <StylesProvider jss={jss}>
          <ThemeProvider theme={theme}>
            {/* CssBaseline kickstart an elegant, consistent, and simple baseline to build upon. */}
            <CssBaseline />
            <FadeTransition title={location.pathname}>
              {!isObjectEmpty(header) && (
                <Header
                  {...props}
                  language={language}
                  data={header}
                  t={t}
                  footerRef={footerNode}
                />
              )}

              {React.Children.map(children, (child) => {
                // checking isValidElement is the safe way
                if (React.isValidElement(child)) {
                  return React.cloneElement(child, {
                    ...props,
                    data: { ...pageData, ...rest },
                    footerNode,
                    t,
                  })
                }
                return child
              })}
              {!isObjectEmpty(footer) && (
                <Footer
                  location={location}
                  {...props}
                  ref={footerRef}
                  data={{ ...footer, waitingList }}
                  t={t}
                />
              )}
            </FadeTransition>
          </ThemeProvider>
        </StylesProvider>
      </GoogleReCaptchaProvider>
    </React.Fragment>
  )
}

export default injectIntl(Layout)
Layout.propTypes = {
  children: PropTypes.node,
}
